/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import * as changeCase from "change-case";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  DELETE_SECTION_OPTION,
  GET_PRESENTATION,
  UPDATE_PRESENTATION,
  UPDATE_SECTION_OPTION,
  UPLOAD_FILE,
} from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { CREATE_SECTION_OPTION } from "../graphql/createSectionOption.mutation";
const is = require("is_js");
const log: any = LoggerModule?.getInstance();

export interface UpdateSectionOptionProps {
  formData: {
    name?: string;
    year?: string;
    status?: string;
  };
  id: string;
}
export interface CreateSectionOptionProps {
  formData: {
    alias?: string;
    description?: string;
    img1?: string;
    img2?: string;
    isVisible?: boolean;
    minDownPayment?: string;
    regMode?: string;
    images?: any;
  };
  id: string;
}
export interface DeleteSectionOptionProps {
  id: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ISectionOptionMethods extends IPluginMethodMap {
  updateSectionOption(args: any): Promise<any>;
  createSectionOption(args: any): Promise<any>;
  deleteSectionOption(args: any): Promise<any>;
}

export class SectionOptionService implements IAgentPlugin {
  readonly methods: ISectionOptionMethods;

  constructor() {
    this.methods = {
      updateSectionOption: this.updateSectionOption.bind(this),
      createSectionOption: this.createSectionOption.bind(this),
      deleteSectionOption: this.deleteSectionOption.bind(this),
    };
  }

  async uploadFile(arr: any, formData: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let imagesUrlArray: any = [];
    let fileType = "jpeg";

    if (arr && is.not.empty(arr)) {
      for (let obj of arr) {
        if (obj && is.existy(obj)) {
          if (this.isValidHttpUrl(obj)) {
            imagesUrlArray.push(obj);
            continue;
          }
          fileType = this.getFileTypeFromBase64(obj) ?? "jpeg";
          try {
            if (client) {
              const res = await client.mutate({
                mutation: UPLOAD_FILE,
                variables: {
                  name: fileType,
                  data: obj,
                  category1: "costumetype",
                  category2: formData?.name ?? "costumeImg",
                  addBandName: true,
                },
                fetchPolicy: "no-cache",
              });
              if (res?.data?.uploadFile) {
                imagesUrlArray.push(res?.data?.uploadFile);
              }
            }
          } catch (err: any) {
            let _display_message =
              err?.graphQLErrors &&
              Array.isArray(err?.graphQLErrors) &&
              err?.graphQLErrors.find((item: any) => item.extensions);
            if (_display_message?.extensions?.display_message) {
              throw Error(_display_message?.extensions?.display_message);
            }

            throw err;
          }
        }
      }
    }

    return imagesUrlArray;
  }

  async createSectionOption(args: CreateSectionOptionProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const imagesArr: [] = await this.uploadFile(args?.images, args);

        if (is.existy(args?.link) && is.not.empty(args?.link)) {
          imagesArr.push(args?.link);
        }

        const res = await client.mutate({
          mutation: CREATE_SECTION_OPTION,
          variables: {
            data: {
              code: args?.code,
              defaultInvites: args?.defaultInvites,
              description: args?.description,
              img1: args?.img1,
              img2: args?.img2,
              isMandatory: args?.isMandatory,
              isSingleFeature: args?.isSingleFeature,
              isVisible: args?.isVisible,
              name: changeCase.capitalCase(args?.name),
              presentation: args?.presentation,
              price: args?.price,
              section: args?.section,
              sectionSpecificInvites: args?.sectionSpecificInvites,
              specialInvites: args?.specialInvites,
              images: imagesArr,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.createCostumeType,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async updateSectionOption(args: UpdateSectionOptionProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        log.blue(`inviteUser server component222:`, args);
        const imagesArr: [] = await this.uploadFile(
          args?.formData?.images,
          args?.formData
        );

        if (
          is.existy(args?.formData?.link) &&
          is.not.empty(args?.formData?.link)
        ) {
          imagesArr.push(args?.formData?.link);
        }

        const res = await client.mutate({
          mutation: UPDATE_SECTION_OPTION,
          variables: {
            filter: {
              _id: args?.id,
            },
            update: {
              code: args?.formData?.code,
              defaultInvites: args?.formData?.defaultInvites,
              description: args?.formData?.description,
              img1: args?.formData?.img1,
              img2: args?.formData?.img2,
              isMandatory: args?.formData?.isMandatory,
              isSingleFeature: args?.formData?.isSingleFeature,
              isVisible: args?.formData?.isVisible,
              name: changeCase.capitalCase(args?.formData?.name),
              price: args?.formData?.price,
              section: args?.formData?.section,
              sectionSpecificInvites: args?.formData?.sectionSpecificInvites,
              specialInvites: args?.formData?.specialInvites,
              images: imagesArr,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.updateCostumeType,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async deleteSectionOption(args: DeleteSectionOptionProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_SECTION_OPTION,
          variables: {
            filter: {
              _id: args?.id,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private isValidHttpUrl(str: string) {
    let url;
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    if (urlRegex.test(str)) {
      return true;
    }
    return false;
  }

  private getFileTypeFromBase64(base64String: string) {
    const matches = base64String.match(/^data:image\/(\w+)/);
    if (matches && matches.length === 2) {
      return matches[1];
    }
    return null; // Invalid base64 string or unable to determine the file type
  }
}
